import React, { useEffect, useState } from 'react';
import { Modal, Input, Alert, Typography, Space, Tag } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Order } from '../../../types/orders';

const { Text } = Typography;
const { TextArea } = Input;

interface CancelOrderModalProps {
  visible: boolean;
  order?: Order;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}

/**
 * Cancelar la orden completa: la salida para las órdenes levantadas por error,
 * que no se pueden cobrar y por tanto tampoco cerrar.
 *
 * Pide motivo obligatorio porque queda en la auditoría junto con quién canceló
 * — es lo que distingue un error de captura de algo que conviene revisar.
 */
const MOTIVOS_FRECUENTES = [
  'Orden levantada por error',
  'Cliente se retiró',
  'Orden duplicada',
  'Prueba del sistema',
];

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  visible,
  order,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (visible) {
      setReason('');
      setTouched(false);
    }
  }, [visible]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= 3;

  const handleOk = async () => {
    setTouched(true);
    if (!isValid) return;
    await onConfirm(trimmed);
  };

  const activeItems = (order?.orderItems || []).filter(i => !i.isCancelled);

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: 'var(--color-error-text)' }} />
          <span>Cancelar orden #{order?.orderNumber}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Sí, cancelar orden"
      cancelText="No"
      okButtonProps={{ danger: true, loading, disabled: !isValid }}
      cancelButtonProps={{ disabled: loading }}
      destroyOnClose
      width={520}
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="La orden se cerrará sin cobrarse"
        description={
          <Space direction="vertical" size={4}>
            <Text>
              No genera pago ni movimiento de caja, y no se puede deshacer. En los
              reportes aparece como orden cancelada.
            </Text>
            {activeItems.length > 0 && (
              <Text type="secondary">
                Quedan {activeItems.length} producto{activeItems.length !== 1 ? 's' : ''} sin
                cancelar por ${(order?.total ?? 0).toFixed(2)}.
              </Text>
            )}
          </Space>
        }
      />

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Motivo de la cancelación
      </Text>

      <Space wrap size={[8, 8]} style={{ marginBottom: 10 }}>
        {MOTIVOS_FRECUENTES.map(m => (
          <Tag.CheckableTag
            key={m}
            checked={trimmed === m}
            onChange={() => {
              setReason(m);
              setTouched(true);
            }}
          >
            {m}
          </Tag.CheckableTag>
        ))}
      </Space>

      <TextArea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="Elige uno de arriba o escribe el motivo"
        rows={2}
        maxLength={500}
        showCount
        autoFocus
        status={touched && !isValid ? 'error' : undefined}
      />
      {touched && !isValid && (
        <Text type="danger" style={{ fontSize: 12 }}>
          Escribe al menos 3 caracteres.
        </Text>
      )}
    </Modal>
  );
};

export default CancelOrderModal;
